import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "custom-notification-container" }
const _hoisted_2 = { class: "notifications-wrapper" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "notification-icon" }
const _hoisted_5 = {
  key: 0,
  class: "fas fa-check-circle"
}
const _hoisted_6 = {
  key: 1,
  class: "fas fa-exclamation-circle"
}
const _hoisted_7 = {
  key: 2,
  class: "fas fa-exclamation-triangle"
}
const _hoisted_8 = {
  key: 3,
  class: "fas fa-info-circle"
}
const _hoisted_9 = { class: "notification-content" }
const _hoisted_10 = { class: "notification-title" }
const _hoisted_11 = { class: "notification-text" }
const _hoisted_12 = ["onClick"]

import { ref, onMounted } from 'vue';

// Simple translation function using window variable
interface Notification {
  id: string;
  title: string;
  text: string;
  type: 'success' | 'error' | 'warning' | 'info';
  show: boolean;
  timeout?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CustomNotification',
  setup(__props, { expose: __expose }) {

const t = (key: string) => {
  try {
    const messages = (window as any).__i18nMessages;
    const locale = (window as any).__i18nLocale || 'en';
    
    if (!messages || !messages[locale]) {
      return key;
    }
    
    // Split the key by dots to navigate nested objects
    const keys = key.split('.');
    let value = messages[locale];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key; // Key not found, return original key
      }
    }
    
    return typeof value === 'string' ? value : key;
  } catch (error) {
    return key;
  }
};

// Function to check if a string looks like a translation key (e.g., "model.key" or "model.some_key")
const isTranslationKey = (str: string): boolean => {
  // Check if it matches pattern like "word.word" or "word.word_word"
  // Must have at least one dot and contain only alphanumeric, underscores, and dots
  return /^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)+$/.test(str);
};

// Function to translate Laravel validation error messages
const translateValidationError = (message: string): string => {
  try {
    // First check if the message itself is a translation key (e.g., "department.Cannot_assign_multiple_manager_jobs_to_the_same_department")
    if (isTranslationKey(message)) {
      const translated = t(message);
      // If translation found (different from key), return it
      if (translated !== message) {
        return translated;
      }
    }

    if (/^The\s+.+?\s+field\s+is\s+required\.$/i.test(message)) {
      return message;
    }
    
    // Common Laravel validation error patterns
    const patterns = [
      // Field is required
      {
        regex: /^The (.+) field is required\.$/i,
        translationKey: 'common.required',
        params: ['attribute']
      },
      // Field must be at least X characters
      {
        regex: /^The (.+) must be at least (\d+) characters\.$/i,
        translationKey: 'common.min',
        params: ['attribute', 'min']
      },
      // Field must not be greater than X characters
      {
        regex: /^The (.+) must not be greater than (\d+) characters\.$/i,
        translationKey: 'common.max',
        params: ['attribute', 'max']
      },
      // Field must be a valid email
      {
        regex: /^The (.+) must be a valid email address\.$/i,
        translationKey: 'common.email',
        params: ['attribute']
      },
      // Field must be unique
      {
        regex: /^The (.+) has already been taken\.$/i,
        translationKey: 'common.unique',
        params: ['attribute']
      },
      // Field must be numeric
      {
        regex: /^The (.+) must be a number\.$/i,
        translationKey: 'common.numeric',
        params: ['attribute']
      },
      // Field must be between X and Y
      {
        regex: /^The (.+) must be between (\d+) and (\d+)\.$/i,
        translationKey: 'common.between',
        params: ['attribute', 'min', 'max']
      },
      // Field must be at least X (numeric)
      {
        regex: /^The (.+) must be at least (\d+)\.$/i,
        translationKey: 'common.min_numeric',
        params: ['attribute', 'min']
      },
      // Field must not be greater than X (numeric)
      {
        regex: /^The (.+) must not be greater than (\d+)\.$/i,
        translationKey: 'common.max_numeric',
        params: ['attribute', 'max']
      },
      // Field must be a valid date
      {
        regex: /^The (.+) is not a valid date\.$/i,
        translationKey: 'common.date',
        params: ['attribute']
      },
      // Field must be confirmed
      {
        regex: /^The (.+) confirmation does not match\.$/i,
        translationKey: 'common.confirmed',
        params: ['attribute']
      }
    ];

    // Try to match the message with known patterns
    for (const pattern of patterns) {
      const match = message.match(pattern.regex);
      if (match) {
        // Get the translation template
        const template = t(pattern.translationKey);
        
        // If template is the same as key, translation not found, return original message
        if (template === pattern.translationKey) {
          return message;
        }
        
        // Replace placeholders with actual values
        let translatedMessage = template;
        pattern.params.forEach((param, index) => {
          let value = match[index + 1]; // +1 because match[0] is the full string
          
          // Translate field names if they exist
          const fieldTranslation = t(`common.${value}`);
          if (fieldTranslation !== `common.${value}`) {
            value = fieldTranslation;
          }
          
          // Replace placeholder
          translatedMessage = translatedMessage.replace(`:${param}`, value);
        });
        
        return translatedMessage;
      }
    }
    
    // If no pattern matches, return the original message
    return message;
  } catch (error) {
    return message;
  }
};

const notifications = ref<Notification[]>([]);

// Success notification
const showSuccess = (action: string, message?: string) => {
  const notification: Notification = {
    id: Date.now().toString(),
    title: `common.${action}_success`,
    text: message ? translateValidationError(message) : t("common.opration_success"),
    type: 'success',
    show: false,
    timeout: 5000
  };
  
  addNotification(notification);
};

// Queued notification (for async operations)
const showQueued = (action: string, message?: string) => {
  const notification: Notification = {
    id: Date.now().toString(),
    title: "Operation Queued",
    text: message || "Operation is being processed in the background. You will be notified when completed.",
    type: 'info',
    show: false,
    timeout: 8000
  };
  
  addNotification(notification);
};

// Error notification
const showError = (action: string, message?: string) => {
  const isHtmlDoc = (s: string) => /<!doctype|<\s*html|<\s*body/i.test(s);
  const extractRequiredSentence = (s: string): string | null => {
    const match = s.match(/The\s+.+?\s+field\s+is\s+required\./i);
    return match?.[0] ? match[0].trim() : null;
  };

  const rawText = message ? translateValidationError(message) : t("common.opration_failed");
  const safeText =
    typeof rawText === "string" && isHtmlDoc(rawText)
      ? extractRequiredSentence(rawText) || "Validation error"
      : rawText;

  const titleKey = `common.${action}_failed`;
  const resolvedTitle = t(titleKey);
  const safeTitle = resolvedTitle === titleKey ? "Validation error" : resolvedTitle;

  const notification: Notification = {
    id: Date.now().toString(),
    title: safeTitle,
    text: safeText,
    type: 'error',
    show: false,
    timeout: 7000
  };
  
  addNotification(notification);
};

// Field-specific error notifications
const showFieldErrors = (action: string, errors: Record<string, string[]>) => {
  for (const field in errors) {
    if (errors.hasOwnProperty(field)) {
      const messages = errors[field];
      messages.forEach((message: string) => {
        const isHtmlDoc = (s: string) => /<!doctype|<\s*html|<\s*body/i.test(s);
        const extractRequiredSentence = (s: string): string | null => {
          const match = s.match(/The\s+.+?\s+field\s+is\s+required\./i);
          return match?.[0] ? match[0].trim() : null;
        };

        const rawText = translateValidationError(message);
        const safeText =
          typeof rawText === "string" && isHtmlDoc(rawText)
            ? extractRequiredSentence(rawText) || "Validation error"
            : rawText;

        const titleKey = `common.${action}_failed`;
        const resolvedTitle = t(titleKey);
        const safeTitle = resolvedTitle === titleKey ? "Validation error" : resolvedTitle;

        const notification: Notification = {
          id: Date.now().toString() + Math.random(),
          title: safeTitle,
          text: safeText,
          type: 'error',
          show: false,
          timeout: 7000
        };
        
        addNotification(notification);
      });
    }
  }
};

// Add notification to the list
const addNotification = (notification: Notification) => {
  notifications.value.push(notification);
  
  // Make notification immediately visible
  notification.show = true;
  
  // Auto remove after timeout
  if (notification.timeout) {
    setTimeout(() => {
      removeNotification(notification.id);
    }, notification.timeout);
  }
};

// Remove notification
const removeNotification = (id: string) => {
  const index = notifications.value.findIndex(n => n.id === id);
  if (index > -1) {
    notifications.value[index].show = false;
    setTimeout(() => {
      notifications.value.splice(index, 1);
    }, 300);
  }
};

// Expose methods for external use
__expose({
  showSuccess,
  showError,
  showFieldErrors,
  showQueued
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(notifications.value, (notification) => {
        return (_openBlock(), _createElementBlock("div", {
          key: notification.id,
          class: _normalizeClass([
          'custom-notification',
          `notification-${notification.type}`,
          notification.show ? 'show' : ''
        ]),
          onClick: ($event: any) => (removeNotification(notification.id))
        }, [
          _createElementVNode("div", _hoisted_4, [
            (notification.type === 'success')
              ? (_openBlock(), _createElementBlock("i", _hoisted_5))
              : (notification.type === 'error')
                ? (_openBlock(), _createElementBlock("i", _hoisted_6))
                : (notification.type === 'warning')
                  ? (_openBlock(), _createElementBlock("i", _hoisted_7))
                  : (_openBlock(), _createElementBlock("i", _hoisted_8))
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, _toDisplayString(t(notification.title)), 1),
            _createElementVNode("div", _hoisted_11, _toDisplayString(t(notification.text)), 1)
          ]),
          _createElementVNode("div", {
            class: "notification-close",
            onClick: _withModifiers(($event: any) => (removeNotification(notification.id)), ["stop"])
          }, [...(_cache[0] || (_cache[0] = [
            _createElementVNode("i", { class: "fas fa-times" }, null, -1)
          ]))], 8, _hoisted_12)
        ], 10, _hoisted_3))
      }), 128))
    ])
  ]))
}
}

})